import React, { useState, useMemo, useEffect } from 'react';
import { CompleteKundali, Language, PlanetName } from '../types/kundali';
import { calculateNumerology, NumerologyAnalysis } from '../utils/numerologyEngine';
import { LAGNA_INSIGHTS, getAuspiciousDirection } from '../utils/basicProfileInterpretations';
import {
  getColorTaxonomy,
  getLifeDomainColorAdvice,
  LifeDomainKey,
  ColorTaxonomyResult,
  DomainColorItem
} from './AuspiciousEssentialsBar';
import { NavtaraView } from './NavtaraView';
import { MuhurtaAdvisor } from './MuhurtaAdvisor';
import { GemstoneAdvisor } from './GemstoneAdvisor';
import { VEDIC_UPAYAS, VedicUpayaDetail } from '../utils/gemstoneAdvisorEngine';
import {
  Sparkles,
  Palette,
  Hash,
  Compass,
  Calendar,
  Clock,
  Gem,
  Shield,
  Info,
  CheckCircle2,
  AlertTriangle,
  Car,
  Wallet,
  Home,
  Building2,
  PenTool,
  Star,
  ChevronRight,
  Flame,
  ArrowRight,
  LayoutGrid,
  Sun,
  Moon,
  BookOpen,
  Award
} from 'lucide-react';

export type ShubhaTabKey =
  | 'overview'
  | 'colors-numbers'
  | 'directions'
  | 'navtara'
  | 'muhurta'
  | 'gemstones'
  | 'remedies';

export interface ShubhaEssentialsHubProps {
  kundali: CompleteKundali;
  lang?: Language;
  initialTab?: ShubhaTabKey;
  // Legacy backward-compatibility props
  initialSection?: 'personal' | 'timing' | 'remedies';
  initialSubView?: 'colors' | 'navtara';
  onNavigateTab?: (tab: string) => void;
}

export const ShubhaEssentialsHub: React.FC<ShubhaEssentialsHubProps> = ({
  kundali,
  lang = 'en',
  initialTab,
  initialSection,
  initialSubView,
  onNavigateTab
}) => {
  const isHi = lang === 'hi';

  // Determine starting tab with backward-compatibility resolution
  const resolveInitialTab = (): ShubhaTabKey => {
    if (initialTab) return initialTab;
    if (initialSection === 'timing') return 'muhurta';
    if (initialSection === 'remedies') return 'gemstones';
    if (initialSection === 'personal') {
      return initialSubView === 'navtara' ? 'navtara' : 'colors-numbers';
    }
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState<ShubhaTabKey>(resolveInitialTab());
  const [activeDomainKey, setActiveDomainKey] = useState<LifeDomainKey>('vehicle');
  const [selectedRemedyPlanet, setSelectedRemedyPlanet] = useState<PlanetName>('Sun');

  // Synchronize with prop changes (e.g., deep linking or router navigation)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    } else if (initialSection) {
      if (initialSection === 'timing') setActiveTab('muhurta');
      else if (initialSection === 'remedies') setActiveTab('gemstones');
      else if (initialSection === 'personal') {
        setActiveTab(initialSubView === 'navtara' ? 'navtara' : 'colors-numbers');
      }
    }
  }, [initialTab, initialSection, initialSubView]);

  // Compute verified Numerology data
  const birthDate = kundali.birthDetails?.date || '1990-01-01';
  const fullName = kundali.birthDetails?.name || 'Jataka';
  const gender = kundali.birthDetails?.gender || 'male';

  const numAnalysis: NumerologyAnalysis = useMemo(() => {
    return calculateNumerology(birthDate, fullName, gender);
  }, [birthDate, fullName, gender]);

  // Lagna Insights
  const lagnaHouse = kundali.charts?.D1?.houses?.find((h) => h.houseNumber === 1) || kundali.houses?.[0];
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const lagnaData = LAGNA_INSIGHTS[lagnaSign] || LAGNA_INSIGHTS.Aries;
  const lagnaContent = isHi ? lagnaData.hi : lagnaData.en;

  // Triadic Color Taxonomy
  const colorTaxonomy: ColorTaxonomyResult = useMemo(() => {
    return getColorTaxonomy(lagnaSign, numAnalysis.moolank, lang, lagnaContent.luckyColor);
  }, [lagnaSign, numAnalysis.moolank, lang, lagnaContent.luckyColor]);

  // Domain Color Advice
  const lifeDomainAdvice: DomainColorItem[] = useMemo(() => {
    return getLifeDomainColorAdvice(lagnaSign, numAnalysis.moolank, lang);
  }, [lagnaSign, numAnalysis.moolank, lang]);

  const activeDomainItem = useMemo(() => {
    return lifeDomainAdvice.find((d) => d.id === activeDomainKey) || lifeDomainAdvice[0];
  }, [lifeDomainAdvice, activeDomainKey]);

  // Directional Guidance
  const auspiciousDirection = useMemo(() => {
    return getAuspiciousDirection(lagnaSign, lang);
  }, [lagnaSign, lang]);

  // Natal Moon Nakshatra (for verified Navtara overview card)
  const moonPlanet = kundali.planets?.find((p) => p.name === 'Moon');
  const moonNakshatraName = moonPlanet?.nakshatra || 'Ashwini';

  // Life Stone (for verified Gemstone overview card)
  const lifeStone = kundali.gemstones?.find((g) => g.type?.toLowerCase().includes('life')) || kundali.gemstones?.[0];
  const lifeStoneName = lifeStone?.gemstone || 'Ruby';

  // Navigation Items
  const navTabs: { id: ShubhaTabKey; labelEn: string; labelHi: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'overview', labelEn: 'Overview', labelHi: 'सामान्य परिचय', icon: LayoutGrid },
    { id: 'colors-numbers', labelEn: 'Colours & Numbers', labelHi: 'शुभ रंग व अंक', icon: Palette },
    { id: 'directions', labelEn: 'Directions', labelHi: 'शुभ दिशाएं', icon: Compass },
    { id: 'navtara', labelEn: 'Navtara', labelHi: 'नवतारा चक्र', icon: Star },
    { id: 'muhurta', labelEn: 'GHD Muhurta', labelHi: 'शुभ मुहूर्त', icon: Clock },
    { id: 'gemstones', labelEn: 'Gemstones', labelHi: 'रत्न परामर्श', icon: Gem },
    { id: 'remedies', labelEn: 'Vedic Remedies', labelHi: 'वैदिक उपाय', icon: Shield }
  ];

  return (
    <div className="space-y-6 bg-[#FFFDF7] dark:bg-[#071F1E] rounded-3xl p-4 sm:p-6 border border-[#B58A4A]/20 shadow-xs" id="shubha-essentials-hub">
      {/* =================================================================== */}
      {/* A. PAGE HEADER (Canonical Design Tokens: #FFFDF7, #174C4B, #B58A4A) */}
      {/* =================================================================== */}
      <header className="relative overflow-hidden rounded-2xl bg-[#174C4B] border border-[#B58A4A]/30 p-5 sm:p-6 text-white shadow-sm">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 w-40 h-40 bg-[#B58A4A]/15 rounded-full blur-2xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-[#B58A4A]/25 text-[#E6C687] border border-[#B58A4A]/40">
                {isHi ? 'वैदिक शुभ तत्व एवं मुहूर्त' : 'Vedic Auspicious Essentials'}
              </span>
              <span className="text-xs text-white/60">•</span>
              <span className="text-xs font-serif text-[#E6C687]">
                {lagnaSign} {isHi ? 'लग्न' : 'Lagna'}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold font-serif text-white tracking-wide flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#B58A4A]" />
              {isHi ? 'शुभ तत्व' : 'Shubha Essentials'}
            </h1>
            <p className="text-xs sm:text-sm text-white/80 max-w-xl mt-1 leading-relaxed">
              {isHi ? 'व्यक्तिगत वैदिक मार्गदर्शन' : 'Personalised Vedic guidance'}
            </p>
          </div>

          {/* Quick Metrics Badge */}
          <div className="grid grid-cols-3 gap-2 bg-black/25 backdrop-blur-xs p-3 rounded-xl border border-white/10 shrink-0 text-center">
            <div className="px-2">
              <span className="text-[10px] text-white/70 block uppercase font-medium">
                {isHi ? 'मूलांक' : 'Moolank'}
              </span>
              <span className="text-base font-bold text-[#B58A4A] font-serif">
                {numAnalysis.moolank}
              </span>
            </div>
            <div className="px-2 border-x border-white/10">
              <span className="text-[10px] text-white/70 block uppercase font-medium">
                {isHi ? 'भाग्यांक' : 'Bhagyank'}
              </span>
              <span className="text-base font-bold text-[#B58A4A] font-serif">
                {numAnalysis.bhagyank}
              </span>
            </div>
            <div className="px-2">
              <span className="text-[10px] text-white/70 block uppercase font-medium">
                {isHi ? 'शुभ दिशा' : 'Direction'}
              </span>
              <span className="text-xs font-bold text-white font-serif leading-tight block mt-0.5">
                {auspiciousDirection}
              </span>
            </div>
          </div>
        </div>

        {/* =================================================================== */}
        {/* B. HORIZONTAL PRIMARY NAVIGATION (7 Destinations, Mobile Scroll)    */}
        {/* =================================================================== */}
        <nav
          className="mt-5 pt-3 border-t border-white/15 flex items-center gap-1.5 overflow-x-auto custom-horizontal-scrollbar pb-1 text-xs font-semibold select-none"
          role="tablist"
          aria-label={isHi ? 'शुभ तत्व नेविगेशन' : 'Shubha Essentials Navigation'}
        >
          {navTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => {
                  setActiveTab(tab.id);
                  onNavigateTab?.(tab.id);
                }}
                className={`px-3 py-2 rounded-xl transition-all shrink-0 flex items-center gap-1.5 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-[#B58A4A] ${
                  isActive
                    ? 'bg-[#B58A4A] text-[#174C4B] font-bold shadow-xs'
                    : 'bg-white/10 text-white hover:bg-white/15 hover:text-white'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#174C4B]' : 'text-white/80'}`} />
                <span>{isHi ? tab.labelHi : tab.labelEn}</span>
              </button>
            );
          })}
        </nav>
      </header>

      {/* =================================================================== */}
      {/* C. OVERVIEW TAB: RESPONSIVE CARD GRID LINKING TO 6 CAPABILITIES     */}
      {/* =================================================================== */}
      {activeTab === 'overview' && (
        <div className="space-y-6 animate-fadeIn" id="shubha-overview-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* 1. Colours & Numbers Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border hover:border-[#B58A4A]/50 rounded-2xl p-5 shadow-2xs transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-[#174C4B]/10 rounded-xl text-[#174C4B] dark:text-[#B58A4A] dark:bg-[#B58A4A]/10">
                    <Palette className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#174C4B] bg-[#174C4B]/5 dark:text-[#B58A4A] px-2 py-0.5 rounded-full border border-[#174C4B]/15">
                    {isHi ? 'तत्व व रंग' : 'Harmonics'}
                  </span>
                </div>
                <h3 className="font-bold text-astro-text font-serif text-base">
                  {isHi ? 'शुभ रंग एवं अंक' : 'Colours & Numbers'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'लग्न व मूलांक आधारित त्रि-स्तरीय शुभ रंग वर्गीकरण, दैनिक रंग एवं अनुकूल अंक श्रृंखला।'
                    : 'Personal triadic color taxonomy (lucky, daily, avoid) and Moolank/Bhagyank harmonic series based on natal chart and date.'}
                </p>
                <div className="flex items-center gap-2 pt-2 border-t border-astro-border text-[11px] text-astro-text font-medium">
                  <span className="text-astro-secondary-text">{isHi ? 'प्रमुख रंग:' : 'Lucky Color:'}</span>
                  <span className="font-bold text-emerald-700 dark:text-emerald-400">{colorTaxonomy.lucky.summary}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('colors-numbers')}
                className="w-full py-2 px-3 bg-[#174C4B] text-white hover:bg-[#174C4B]/90 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>{isHi ? 'रंग व अंक देखें' : 'Explore Colours & #'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* 2. Directions Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border hover:border-[#B58A4A]/50 rounded-2xl p-5 shadow-2xs transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-[#174C4B]/10 rounded-xl text-[#174C4B] dark:text-[#B58A4A] dark:bg-[#B58A4A]/10">
                    <Compass className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#174C4B] bg-[#174C4B]/5 dark:text-[#B58A4A] px-2 py-0.5 rounded-full border border-[#174C4B]/15">
                    {lagnaSign} Lagna
                  </span>
                </div>
                <h3 className="font-bold text-astro-text font-serif text-base">
                  {isHi ? 'शुभ कार्य दिशाएं' : 'Auspicious Directions'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'लग्न तत्व आधारित मुख्य दिशाएं, कार्यक्षेत्र अभिविन्यास एवं वास्तु ऊर्जा संतुलन।'
                    : 'Classical Lagna-based spatial orientation, elemental energy alignment, and workplace positioning.'}
                </p>
                <div className="flex items-center gap-2 pt-2 border-t border-astro-border text-[11px] text-astro-text font-medium">
                  <span className="text-astro-secondary-text">{isHi ? 'शुभ दिशा:' : 'Favorable:'}</span>
                  <span className="font-bold text-astro-primary">{auspiciousDirection}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('directions')}
                className="w-full py-2 px-3 bg-[#174C4B] text-white hover:bg-[#174C4B]/90 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>{isHi ? 'दिशा मार्गदर्शन देखें' : 'View Directions'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* 3. Navtara Chakram Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border hover:border-[#B58A4A]/50 rounded-2xl p-5 shadow-2xs transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-600 dark:text-amber-400">
                    <Star className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-500/10 dark:text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/20">
                    27 Nakshatras
                  </span>
                </div>
                <h3 className="font-bold text-astro-text font-serif text-base">
                  {isHi ? 'नवतारा चक्र' : 'Navtara Chakram'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'जन्मनक्षत्र से २७ नक्षत्रों का ९ ताराओं में शास्त्रीय वर्गीकरण (सम्पत, क्षेम, साधक आदि)।'
                    : '9-Tara matrix of 27 Nakshatras from your Janma Nakshatra for daily electional auspiciousness.'}
                </p>
                <div className="flex items-center gap-2 pt-2 border-t border-astro-border text-[11px] text-astro-text font-medium">
                  <span className="text-astro-secondary-text">{isHi ? 'जन्मनक्षत्र:' : 'Janma Nakshatra:'}</span>
                  <span className="font-bold text-amber-700 dark:text-amber-400">{moonNakshatraName}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('navtara')}
                className="w-full py-2 px-3 bg-[#174C4B] text-white hover:bg-[#174C4B]/90 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>{isHi ? 'नवतारा चक्र खोलें' : 'Examine Navtara Chakram'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* 4. GHD Muhurta Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border hover:border-[#B58A4A]/50 rounded-2xl p-5 shadow-2xs transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-[#174C4B]/10 rounded-xl text-[#174C4B] dark:text-[#B58A4A] dark:bg-[#B58A4A]/10">
                    <Clock className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#174C4B] bg-[#174C4B]/5 dark:text-[#B58A4A] px-2 py-0.5 rounded-full border border-[#174C4B]/15">
                    Electional
                  </span>
                </div>
                <h3 className="font-bold text-astro-text font-serif text-base">
                  {isHi ? 'शुभ मुहूर्त शोधन (GHD)' : 'GHD Muhurta Advisor'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'यात्रा, नवीन व्यवसाय, अनुबंध, संपत्ति क्रय एवं संस्कारों के लिए शास्त्रीय मुहूर्त शोधन।'
                    : 'Classical electional astrology and Shubh Ghadi determination for travel, business, ceremony, vehicle, and property.'}
                </p>
                <div className="flex items-center gap-2 pt-2 border-t border-astro-border text-[11px] text-astro-text font-medium">
                  <span className="text-astro-secondary-text">{isHi ? 'पद्धति:' : 'Methodology:'}</span>
                  <span className="font-bold text-astro-text">Panchanga & Tara Bala</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('muhurta')}
                className="w-full py-2 px-3 bg-[#174C4B] text-white hover:bg-[#174C4B]/90 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>{isHi ? 'मुहूर्त शोधन खोलें' : 'Open Muhurta Advisor'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* 5. Gemstone Advisor Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border hover:border-[#B58A4A]/50 rounded-2xl p-5 shadow-2xs transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-emerald-500/10 rounded-xl text-emerald-600 dark:text-emerald-400">
                    <Gem className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-500/10 dark:text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/20">
                    11-Factor Audit
                  </span>
                </div>
                <h3 className="font-bold text-astro-text font-serif text-base">
                  {isHi ? 'रत्न परामर्श' : 'Gemstone Advisor'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'पराशरीय अनुकूल रत्न शास्त्र, उद्देश्य अनुसार रत्न चयन, विरोधी रत्नों की जांच व धारण विधि।'
                    : '11-factor Parashari Anukul gemstone suitability audit, wearing rules, and jewelry conflict auditor.'}
                </p>
                <div className="flex items-center gap-2 pt-2 border-t border-astro-border text-[11px] text-astro-text font-medium">
                  <span className="text-astro-secondary-text">{isHi ? 'जीव रत्न:' : 'Life Stone:'}</span>
                  <span className="font-bold text-emerald-700 dark:text-emerald-400">{lifeStoneName}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('gemstones')}
                className="w-full py-2 px-3 bg-[#174C4B] text-white hover:bg-[#174C4B]/90 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>{isHi ? 'रत्न परामर्श खोलें' : 'Consult Gemstones'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* 6. Vedic Remedies Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border hover:border-[#B58A4A]/50 rounded-2xl p-5 shadow-2xs transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-rose-500/10 rounded-xl text-rose-600 dark:text-rose-400">
                    <Shield className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-500/10 dark:text-rose-300 px-2 py-0.5 rounded-full border border-rose-500/20">
                    Navagraha
                  </span>
                </div>
                <h3 className="font-bold text-astro-text font-serif text-base">
                  {isHi ? 'वैदिक उपाय एवं शांति' : 'Vedic Remedies'}
                </h3>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? '९ ग्रहों के बीज एवं वैदिक मंत्र, दान सामग्री, व्रत विधि, रुद्राक्ष एवं दैनिक सात्विक आचरण।'
                    : 'Complete Navagraha remedial guidance encompassing Vedic Mantras, Daana (charity), Vrata (fasting), Rudraksha, and Yantras.'}
                </p>
                <div className="flex items-center gap-2 pt-2 border-t border-astro-border text-[11px] text-astro-text font-medium">
                  <span className="text-astro-secondary-text">{isHi ? 'कवरेज:' : 'Coverage:'}</span>
                  <span className="font-bold text-rose-700 dark:text-rose-400">{isHi ? 'सम्पूर्ण ९ ग्रह' : 'All 9 Navagrahas'}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('remedies')}
                className="w-full py-2 px-3 bg-[#174C4B] text-white hover:bg-[#174C4B]/90 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              >
                <span>{isHi ? 'वैदिक उपाय देखें' : 'Explore Vedic Remedies'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 2. DEDICATED DESTINATION: COLOURS & NUMBERS                         */}
      {/* =================================================================== */}
      {activeTab === 'colors-numbers' && (
        <div className="space-y-6 animate-fadeIn" id="shubha-colors-numbers-panel">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Triadic Color Taxonomy */}
            <div className="lg:col-span-2 bg-white dark:bg-[#0C2A29] border border-astro-border rounded-2xl p-5 shadow-2xs space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#174C4B] dark:text-[#B58A4A]" />
                  <div>
                    <h3 className="font-bold text-astro-text font-serif text-base">
                      {isHi ? 'त्रि-स्तरीय शुभ रंग वर्गीकरण' : 'Triadic Astrological Color Taxonomy'}
                    </h3>
                    <p className="text-[11px] text-astro-secondary-text">
                      {isHi
                        ? 'लग्न स्वामी एवं मूलांक स्वामी के खगोलीय रश्मि सामंजस्य पर आधारित'
                        : 'Synthesized from Lagna Lord & Moolank planetary ray harmonic balance'}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 bg-astro-bg rounded-lg border border-astro-border text-astro-primary">
                  {lagnaSign}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Favorable */}
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {isHi ? 'शुभ व फलदायी' : 'Favorable / Lucky'}
                    </span>
                    <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-950 px-1.5 py-0.5 rounded">
                      {colorTaxonomy.lucky.items.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {colorTaxonomy.lucky.items.map((c, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800/50 text-astro-text shadow-2xs"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full border border-black/10 shrink-0"
                          style={{ backgroundColor: c.hex }}
                        />
                        {c.name}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed pt-1">
                    {colorTaxonomy.lucky.advice}
                  </p>
                </div>

                {/* Secondary/Neutral */}
                <div className="bg-slate-500/5 border border-slate-500/20 rounded-xl p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-slate-500" />
                      {isHi ? 'दैनिक व सामान्य' : 'Secondary / Daily'}
                    </span>
                    <span className="text-[10px] font-bold text-slate-600 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                      {colorTaxonomy.neutral.items.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {colorTaxonomy.neutral.items.map((c, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-astro-text shadow-2xs"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full border border-black/10 shrink-0"
                          style={{ backgroundColor: c.hex }}
                        />
                        {c.name}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed pt-1">
                    {colorTaxonomy.neutral.advice}
                  </p>
                </div>

                {/* Avoid/Cautionary */}
                <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-rose-700 dark:text-rose-400 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {isHi ? 'वर्जित व विरोधी' : 'Caution / Inimical'}
                    </span>
                    <span className="text-[10px] font-bold text-rose-600 bg-rose-100 dark:bg-rose-950 px-1.5 py-0.5 rounded">
                      {colorTaxonomy.avoid.items.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {colorTaxonomy.avoid.items.map((c, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-900/50 text-astro-text shadow-2xs"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full border border-black/10 shrink-0"
                          style={{ backgroundColor: c.hex }}
                        />
                        {c.name}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed pt-1">
                    {colorTaxonomy.avoid.advice}
                  </p>
                </div>
              </div>

              {/* Rationale and Upaya */}
              <div className="p-3 bg-astro-bg rounded-xl border border-astro-border text-xs space-y-1.5">
                <p className="text-astro-text">
                  <span className="font-bold text-astro-primary">{isHi ? 'शास्त्रीय तर्क:' : 'Classical Rationale:'} </span>
                  {colorTaxonomy.lucky.rationale}
                </p>
                {colorTaxonomy.remedyIfWorn && (
                  <p className="text-astro-secondary-text pt-1 border-t border-astro-border">
                    <span className="font-bold text-amber-700 dark:text-amber-400">{isHi ? 'अनिवार्यता पर उपाय:' : 'Balancing Upaya:'} </span>
                    {colorTaxonomy.remedyIfWorn}
                  </p>
                )}
              </div>
            </div>

            {/* Auspicious Numbers Card */}
            <div className="bg-white dark:bg-[#0C2A29] border border-astro-border rounded-2xl p-5 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 border-b border-astro-border pb-3">
                <Hash className="w-5 h-5 text-[#174C4B] dark:text-[#B58A4A]" />
                <div>
                  <h3 className="font-bold text-astro-text font-serif text-base">
                    {isHi ? 'शुभ अंक विवरण' : 'Auspicious Numbers'}
                  </h3>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi ? 'जन्मतिथि आधारित मूलांक एवं भाग्यांक' : 'Birth Date Root & Destiny Harmonics'}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-astro-bg rounded-xl border border-astro-border">
                  <div>
                    <span className="text-[10px] text-astro-secondary-text block font-bold uppercase tracking-wider">
                      {isHi ? 'मूलांक (Root #)' : 'Moolank (Driver #)'}
                    </span>
                    <span className="text-xs font-semibold text-astro-text">
                      {isHi ? `स्वामी: ${numAnalysis.moolankLordHi}` : `Lord: ${numAnalysis.moolankLord}`}
                    </span>
                  </div>
                  <span className="text-2xl font-bold font-serif text-[#174C4B] dark:text-[#B58A4A] bg-[#174C4B]/10 w-10 h-10 rounded-full flex items-center justify-center border border-[#174C4B]/20">
                    {numAnalysis.moolank}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-astro-bg rounded-xl border border-astro-border">
                  <div>
                    <span className="text-[10px] text-astro-secondary-text block font-bold uppercase tracking-wider">
                      {isHi ? 'भाग्यांक (Destiny #)' : 'Bhagyank (Destiny #)'}
                    </span>
                    <span className="text-xs font-semibold text-astro-text">
                      {isHi ? `स्वामी: ${numAnalysis.bhagyankLordHi}` : `Lord: ${numAnalysis.bhagyankLord}`}
                    </span>
                  </div>
                  <span className="text-2xl font-bold font-serif text-emerald-600 bg-emerald-500/10 w-10 h-10 rounded-full flex items-center justify-center border border-emerald-500/30">
                    {numAnalysis.bhagyank}
                  </span>
                </div>

                <div className="p-3 bg-astro-bg rounded-xl border border-astro-border space-y-1.5">
                  <span className="text-[10px] text-astro-secondary-text block font-bold uppercase tracking-wider">
                    {isHi ? 'अनुकूल शुभ अंक श्रृंखला' : 'Harmonic Auspicious Series'}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {numAnalysis.luckyNumbers.map((n, idx) => (
                      <span
                        key={idx}
                        className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-bold font-serif text-xs flex items-center justify-center border border-emerald-500/20 shadow-2xs"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Practical Life Domain Color Guidance */}
          <div className="bg-white dark:bg-[#0C2A29] border border-astro-border rounded-2xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#B58A4A]" />
                <div>
                  <h3 className="font-bold text-astro-text font-serif text-base">
                    {isHi ? 'जीवन क्षेत्र अनुसार व्यावहारिक रंग चयन' : 'Practical Life-Domain Color Advice'}
                  </h3>
                  <p className="text-[11px] text-astro-secondary-text">
                    {isHi
                      ? 'वाहन, आवास, कार्यालय, पर्स व ब्रांडिंग के लिए शास्त्रीय रंग परामर्श'
                      : 'Specific color recommendations for vehicles, home interiors, office desk, and branding'}
                  </p>
                </div>
              </div>
            </div>

            {/* Domain Selector Pills */}
            <div className="flex gap-2 overflow-x-auto pb-1 text-xs font-semibold">
              {lifeDomainAdvice.map((domain) => {
                const isSelected = domain.id === activeDomainKey;
                return (
                  <button
                    key={domain.id}
                    type="button"
                    onClick={() => setActiveDomainKey(domain.id)}
                    className={`px-3.5 py-2 rounded-xl transition-all shrink-0 cursor-pointer flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-[#174C4B] text-white shadow-2xs font-bold'
                        : 'bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-text'
                    }`}
                  >
                    {domain.id === 'vehicle' && <Car className="w-3.5 h-3.5" />}
                    {domain.id === 'wallet' && <Wallet className="w-3.5 h-3.5" />}
                    {domain.id === 'home' && <Home className="w-3.5 h-3.5" />}
                    {domain.id === 'office' && <Building2 className="w-3.5 h-3.5" />}
                    {domain.id === 'branding' && <PenTool className="w-3.5 h-3.5" />}
                    <span>{isHi ? domain.titleHi : domain.titleEn}</span>
                  </button>
                );
              })}
            </div>

            {/* Active Domain Details */}
            {activeDomainItem && (
              <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border pb-2.5">
                  <div>
                    <h4 className="text-sm font-bold text-astro-primary font-serif">
                      {isHi ? activeDomainItem.titleHi : activeDomainItem.titleEn}
                    </h4>
                    <span className="text-[11px] text-astro-secondary-text">
                      {isHi ? activeDomainItem.bhavaHi : activeDomainItem.bhavaEn}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg space-y-1.5">
                    <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider block">
                      {isHi ? 'अनुशंसित रंग (Recommended)' : 'Recommended Colors'}
                    </span>
                    <p className="font-semibold text-astro-text">
                      {(isHi ? activeDomainItem.recommendedColorsHi : activeDomainItem.recommendedColorsEn).join(', ')}
                    </p>
                  </div>

                  <div className="p-3 bg-rose-500/5 border border-rose-500/20 rounded-lg space-y-1.5">
                    <span className="text-[10px] font-bold text-rose-700 dark:text-rose-400 uppercase tracking-wider block">
                      {isHi ? 'वर्जित रंग (Avoid)' : 'Colors to Avoid'}
                    </span>
                    <p className="font-semibold text-astro-text">
                      {(isHi ? activeDomainItem.avoidColorsHi : activeDomainItem.avoidColorsEn).join(', ')}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-astro-text leading-relaxed">
                  <span className="font-bold text-astro-primary">{isHi ? 'मार्गदर्शन:' : 'Guidance:'} </span>
                  {isHi ? activeDomainItem.guidanceHi : activeDomainItem.guidanceEn}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 3. DEDICATED DESTINATION: DIRECTIONS                                */}
      {/* =================================================================== */}
      {activeTab === 'directions' && (
        <div className="space-y-6 animate-fadeIn" id="shubha-directions-panel">
          <div className="bg-white dark:bg-[#0C2A29] border border-astro-border rounded-2xl p-5 sm:p-6 shadow-2xs space-y-5">
            <div className="flex items-center gap-3 border-b border-astro-border pb-4">
              <div className="p-3 bg-[#174C4B]/10 rounded-xl text-[#174C4B] dark:text-[#B58A4A]">
                <Compass className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-astro-text font-serif text-lg">
                  {isHi ? 'शास्त्रीय लग्न दिशा एवं वास्तु ऊर्जा' : 'Classical Lagna Direction & Spatial Energy'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'आपकी जन्मपत्रिका के अनुसार मुख्य कार्य दिशा एवं संतुलन'
                    : 'Auspicious orientation based on natal Lagna triplicity and cardinal elements'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block">
                  {isHi ? 'मुख्य शुभ दिशा (Primary Orientation)' : 'Primary Orientation'}
                </span>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold font-serif text-[#174C4B] dark:text-[#B58A4A]">
                    {auspiciousDirection}
                  </span>
                  <span className="px-2.5 py-1 bg-astro-card rounded-lg border border-astro-border text-xs font-semibold text-astro-primary">
                    {lagnaSign} Lagna
                  </span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? `आपके लग्न (${lagnaSign}) के अनुसार ${auspiciousDirection} दिशा ऊर्जावान और शुभ मानी जाती है। महत्वपूर्ण वार्ता, ध्यान, अध्ययन एवं कार्य निष्पादन के समय इस दिशा की ओर मुख करना अनुकूल फल देता है।`
                    : `Based on your ${lagnaSign} Lagna, the ${auspiciousDirection} direction aligns with your primary vitality. Facing ${auspiciousDirection} during critical negotiations, study, and creative focus harmonizes mental acuity.`}
                </p>
              </div>

              <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text block">
                  {isHi ? 'व्यावहारिक अनुप्रयोग (Practical Guidelines)' : 'Practical Applications'}
                </span>
                <ul className="text-xs text-astro-text space-y-2 list-disc list-inside leading-relaxed">
                  <li>
                    <strong>{isHi ? 'कार्य डेस्क:' : 'Work Desk:'}</strong> {isHi ? 'मुख्य कार्य करते समय शुभ दिशा की ओर मुख रखें।' : `Position your primary work desk so you face ${auspiciousDirection}.`}
                  </li>
                  <li>
                    <strong>{isHi ? 'ध्यान व पूजा:' : 'Meditation & Prayer:'}</strong> {isHi ? 'ईशान कोण या पूर्व दिशा की ओर मुख कर साधना करें।' : 'Face East or North-East for daily spiritual contemplation and grounding.'}
                  </li>
                  <li>
                    <strong>{isHi ? 'आराम व शयन:' : 'Rest & Sleep:'}</strong> {isHi ? 'दक्षिण या पूर्व दिशा में सिर रखकर सोना स्वास्थ्यवर्धक है।' : 'Sleep with head oriented towards South or East for circadian equilibrium.'}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 4. DEDICATED DESTINATION: NAVTARA CHAKRAM (REUSED AS-IS)            */}
      {/* =================================================================== */}
      {activeTab === 'navtara' && (
        <div className="space-y-4 animate-fadeIn" id="shubha-navtara-panel">
          <div className="p-3.5 bg-white dark:bg-[#0C2A29] border border-astro-border rounded-xl flex items-center justify-between text-xs text-astro-secondary-text">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 shrink-0" />
              <span>
                {isHi
                  ? 'जन्मनक्षत्र से २७ नक्षत्रों का ९ ताराओं (जन्म, सम्पत, विपत, क्षेम, प्रत्यक, साधक, वध, मित्र, परम मित्र) में विभाजन।'
                  : 'Canonical 9-Tara matrix of 27 Nakshatras evaluated from Janma Nakshatra.'}
              </span>
            </div>
            <span className="font-semibold text-astro-primary">{moonNakshatraName}</span>
          </div>

          {/* Canonical NavtaraView Component Reused Unchanged */}
          <NavtaraView kundali={kundali} lang={lang} />
        </div>
      )}

      {/* =================================================================== */}
      {/* 5. DEDICATED DESTINATION: GHD MUHURTA ADVISOR (REUSED AS-IS)        */}
      {/* =================================================================== */}
      {activeTab === 'muhurta' && (
        <div className="space-y-4 animate-fadeIn" id="shubha-muhurta-panel">
          <div className="p-3.5 bg-white dark:bg-[#0C2A29] border border-astro-border rounded-xl flex items-center justify-between text-xs text-astro-secondary-text">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#174C4B] dark:text-[#B58A4A] shrink-0" />
              <span>
                {isHi
                  ? 'शास्त्रीय चुनाव ज्योतिष: यात्रा, व्यवसाय, गृह प्रवेश, वाहन व संपत्ति क्रय के लिए शुभ घटी।'
                  : 'Classical electional Muhurta analysis for travel, business, ceremony, vehicle, and property.'}
              </span>
            </div>
            <span className="font-semibold text-astro-primary">GHD Engine</span>
          </div>

          {/* Canonical MuhurtaAdvisor Component Reused Unchanged */}
          <MuhurtaAdvisor kundali={kundali} lang={lang} />
        </div>
      )}

      {/* =================================================================== */}
      {/* 6. DEDICATED DESTINATION: GEMSTONES (REUSED AS-IS)                 */}
      {/* =================================================================== */}
      {activeTab === 'gemstones' && (
        <div className="space-y-4 animate-fadeIn" id="shubha-gemstones-panel">
          <div className="p-3.5 bg-white dark:bg-[#0C2A29] border border-astro-border rounded-xl flex items-center justify-between text-xs text-astro-secondary-text">
            <div className="flex items-center gap-2">
              <Gem className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                {isHi
                  ? 'पराशरीय अनुकूल रत्न शास्त्र, ११-बिंदु परीक्षण एवं रत्न विरोध विश्लेषक ("वर्तमान में क्या पहना है?")।'
                  : 'Parashari Anukul 11-factor gemstone suitability audit, wearing rules, and jewelry conflict auditor.'}
              </span>
            </div>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">{lifeStoneName}</span>
          </div>

          {/* Canonical GemstoneAdvisor Component Reused Unchanged */}
          <GemstoneAdvisor kundali={kundali} lang={lang} />
        </div>
      )}

      {/* =================================================================== */}
      {/* 7. DEDICATED DESTINATION: VEDIC REMEDIES (UPAYAS BANK)              */}
      {/* =================================================================== */}
      {activeTab === 'remedies' && (
        <div className="space-y-6 animate-fadeIn" id="shubha-remedies-panel">
          <div className="bg-white dark:bg-[#0C2A29] border border-astro-border rounded-2xl p-5 sm:p-6 shadow-2xs space-y-5">
            <div className="flex items-center justify-between border-b border-astro-border pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-rose-500/10 rounded-xl text-rose-600 dark:text-rose-400">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-astro-text font-serif text-lg">
                    {isHi ? 'सम्पूर्ण नवग्रह वैदिक उपाय बैंक' : 'Canonical Navagraha Vedic Upayas Bank'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? 'बीज मंत्र, वैदिक मंत्र, दान सामग्री, व्रत विधि, रुद्राक्ष एवं दैनिक सात्विक आचरण'
                      : 'Authentic Shastric remedies: Beeja & Vedic Mantras, Daana (charity), Vrata (fasting), Rudraksha, and Yantras'}
                  </p>
                </div>
              </div>
            </div>

            {/* Navagraha Planet Selector Pills */}
            <div className="flex gap-1.5 overflow-x-auto pb-1 text-xs font-semibold custom-horizontal-scrollbar">
              {(['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'] as PlanetName[]).map((planet) => {
                const isSelected = selectedRemedyPlanet === planet;
                return (
                  <button
                    key={planet}
                    type="button"
                    onClick={() => setSelectedRemedyPlanet(planet)}
                    className={`px-3 py-1.5 rounded-xl transition-all shrink-0 cursor-pointer flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-[#174C4B] text-white shadow-2xs font-bold'
                        : 'bg-astro-bg border border-astro-border text-astro-secondary-text hover:text-astro-text'
                    }`}
                  >
                    <span>{planet}</span>
                  </button>
                );
              })}
            </div>

            {/* Selected Planet Upaya Details */}
            {(() => {
              const upaya: VedicUpayaDetail | undefined = VEDIC_UPAYAS[selectedRemedyPlanet];
              if (!upaya) return null;

              return (
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Mantras Box */}
                    <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-2.5">
                      <div className="flex items-center gap-2 border-b border-astro-border pb-1.5">
                        <Flame className="w-4 h-4 text-amber-500" />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary font-serif">
                          {isHi ? 'मंत्र साधना' : 'Mantra Sadhana'}
                        </h4>
                      </div>
                      <div className="space-y-1.5 text-xs">
                        <p className="font-serif text-[#174C4B] dark:text-[#E6C687] font-semibold leading-relaxed">
                          {upaya.beejaMantra}
                        </p>
                        <p className="font-serif italic text-astro-secondary-text text-[11px] leading-relaxed">
                          {upaya.vedicMantra}
                        </p>
                        <div className="flex justify-between items-center pt-1 text-[11px] text-astro-secondary-text border-t border-astro-border">
                          <span>{isHi ? 'जप संख्या:' : 'Count:'} {upaya.japCount}</span>
                          <span>{isHi ? 'माला:' : 'Mala:'} {upaya.mala}</span>
                        </div>
                      </div>
                    </div>

                    {/* Daana (Charity) Box */}
                    <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-2.5">
                      <div className="flex items-center gap-2 border-b border-astro-border pb-1.5">
                        <Award className="w-4 h-4 text-emerald-600" />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary font-serif">
                          {isHi ? 'दान सामग्री (Charity)' : 'Daana (Charity)'}
                        </h4>
                      </div>
                      <div className="space-y-1.5 text-xs text-astro-text">
                        <p className="font-medium">
                          {upaya.daana.items.join(', ')}
                        </p>
                        <div className="pt-1 text-[11px] space-y-1 text-astro-secondary-text border-t border-astro-border">
                          <div><strong className="text-astro-text">{isHi ? 'दिन व समय: ' : 'Day & Time: '}</strong>{upaya.daana.day}, {upaya.daana.time}</div>
                          <div><strong className="text-astro-text">{isHi ? 'पात्र: ' : 'Recipient: '}</strong>{upaya.daana.recipient}</div>
                        </div>
                      </div>
                    </div>

                    {/* Vrata (Fasting) & Deity */}
                    <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-2.5">
                      <div className="flex items-center gap-2 border-b border-astro-border pb-1.5">
                        <Calendar className="w-4 h-4 text-purple-600" />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary font-serif">
                          {isHi ? 'व्रत एवं आराध्य देव' : 'Vrata & Deity'}
                        </h4>
                      </div>
                      <div className="space-y-1 text-xs text-astro-text">
                        <div className="font-semibold text-astro-primary">{upaya.vrata.day} (Deity: {upaya.vrata.deity})</div>
                        <p className="text-[11px] text-astro-secondary-text leading-relaxed">{upaya.vrata.method}</p>
                      </div>
                    </div>

                    {/* Rudraksha & Yantra */}
                    <div className="p-4 bg-astro-bg rounded-xl border border-astro-border space-y-2.5">
                      <div className="flex items-center gap-2 border-b border-astro-border pb-1.5">
                        <Shield className="w-4 h-4 text-rose-600" />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary font-serif">
                          {isHi ? 'रुद्राक्ष एवं यंत्र' : 'Rudraksha & Yantra'}
                        </h4>
                      </div>
                      <div className="space-y-1.5 text-xs text-astro-text">
                        <div>
                          <strong className="text-astro-primary font-serif">{upaya.rudraksha.mukhi}</strong>
                          <p className="text-[11px] text-astro-secondary-text">{upaya.rudraksha.benefits}</p>
                        </div>
                        <div className="pt-1 border-t border-astro-border text-[11px]">
                          <strong>{isHi ? 'यंत्र: ' : 'Yantra: '}</strong>{upaya.yantra}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Daily Lifestyle Habits */}
                  {upaya.lifestyle && upaya.lifestyle.length > 0 && (
                    <div className="p-4 bg-astro-card rounded-xl border border-astro-border space-y-2 text-xs">
                      <h4 className="font-bold text-astro-primary font-serif">
                        {isHi ? 'दैनिक सात्विक आचरण एवं सावधानियां:' : 'Daily Sattvic Practices & Conduct:'}
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-astro-text leading-relaxed">
                        {upaya.lifestyle.map((habit, i) => (
                          <li key={i}>{habit}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
